import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authGet } from '../utils/api';
import { Progress } from '../components/ui/progress';
import { 
  TrendUp, 
  TrendDown, 
  Wallet, 
  Target, 
  Sparkle,
  ArrowRight,
  Plus,
  ShoppingCart,
  HeartHalf,
  Lightning,
  ChartPie,
  ChartBar
} from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import {
  PieChart, Pie, Cell, ResponsiveContainer,
  LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, Legend
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CURRENCY_SYMBOLS = {
  USD: '$', EUR: '€', GBP: '£', CAD: 'C$', AUD: 'A$', INR: '₹', JPY: '¥'
};

const PIE_COLORS = ['#3B82F6', '#8B5CF6', '#10B981', '#F59E0B', '#EF4444', '#EC4899', '#06B6D4'];

const CHART_TOOLTIP_STYLE = { background: '#1F2937', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px', fontSize: '12px', boxShadow: '0 8px 24px rgba(0,0,0,0.4)' };
const CHART_ITEM_STYLE = { color: '#F9FAFB' };
const AXIS_TICK = { fill: '#6B7280', fontSize: 11 };
const AXIS_TICK_SM = { fill: '#6B7280', fontSize: 10 };
const INCOME_DOT = { r: 3, fill: '#10B981', strokeWidth: 0 };
const INCOME_ACTIVE_DOT = { r: 5, strokeWidth: 0 };
const EXPENSE_DOT = { r: 3, fill: '#EF4444', strokeWidth: 0 };
const EXPENSE_ACTIVE_DOT = { r: 5, strokeWidth: 0 };
const BAR_RADIUS = [4, 4, 0, 0];
const LEGEND_STYLE = { fontSize: '11px', paddingTop: '8px' };

export default function Dashboard() {
  const { user } = useAuth();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [insights, setInsights] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [insightsLoading, setInsightsLoading] = useState(true);

  const currency = user?.currency || 'USD';
  const symbol = CURRENCY_SYMBOLS[currency] || '$';

  const fetchDashboard = useCallback(async () => {
    try {
      const { data } = await authGet('/api/dashboard');
      setData(data);
    } catch (e) {
      console.error('Dashboard fetch error:', e);
    }
    setLoading(false);
  }, []);

  const fetchCachedInsights = useCallback(async () => {
    try {
      const { data } = await authGet('/api/insights/cached');
      setInsights(data);
    } catch (e) {
      console.error('Cached insights fetch error:', e);
    }
    setInsightsLoading(false);
  }, []);

  const fetchAnalytics = useCallback(async () => {
    try {
      const { data } = await authGet('/api/analytics');
      setAnalytics(data);
    } catch (e) {
      console.error('Analytics fetch error:', e);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
    fetchCachedInsights();
    fetchAnalytics();
  }, [location.key, fetchDashboard, fetchCachedInsights, fetchAnalytics]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-[#3B82F6]" />
      </div>
    );
  }

  const formatCurrency = (amount) => {
    return `${symbol}${(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">
            Hey, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-[#9CA3AF] mt-1 text-sm">Here's your financial overview</p>
        </div>
        <Link
          to="/transactions"
          className="btn-primary flex items-center gap-2 text-sm py-2.5 px-4"
          data-testid="add-transaction-btn"
        >
          <Plus size={16} weight="bold" />
          <span className="hidden sm:inline">Add</span>
        </Link>
      </div>

      {/* Main Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { 
            label: 'Income', icon: TrendUp, color: '#10B981', bg: 'bg-emerald-500/10',
            value: formatCurrency(data?.total_income), testId: 'income-card'
          },
          { 
            label: 'Expenses', icon: TrendDown, color: '#EF4444', bg: 'bg-red-500/10',
            value: formatCurrency(data?.total_expenses), testId: 'expenses-card'
          },
          { 
            label: 'Remaining', icon: Wallet, color: (data?.remaining || 0) >= 0 ? '#3B82F6' : '#EF4444',
            bg: (data?.remaining || 0) >= 0 ? 'bg-blue-500/10' : 'bg-red-500/10',
            value: formatCurrency(data?.remaining), testId: 'remaining-card'
          },
          { 
            label: 'Savings', icon: Target, color: '#8B5CF6', bg: 'bg-violet-500/10',
            value: `${Math.round(data?.savings_progress?.percentage || 0)}%`, testId: 'savings-card'
          },
        ].map(({ label, icon: Icon, color, bg, value, testId }) => (
          <div key={testId} className="card-default p-5" data-testid={testId}>
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center`}>
                <Icon size={18} weight="duotone" style={{ color }} />
              </div>
              <span className="text-sm text-[#9CA3AF]">{label}</span>
            </div>
            <p className="text-xl sm:text-2xl font-semibold tabular-nums" style={{ color }}>
              {value}
            </p>
          </div>
        ))}
      </div>

      {/* Health Score + AI Insights */}
      <div className="grid lg:grid-cols-3 gap-5">
        {/* Health Score */}
        <div className="card-default p-6" data-testid="health-score-card">
          <div className="flex items-center gap-2.5 mb-5">
            <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
              <HeartHalf size={18} weight="duotone" className="text-[#3B82F6]" />
            </div>
            <h3 className="font-semibold text-sm">Financial Health</h3>
          </div>
          {analytics?.health_score ? (
            <div className="flex flex-col items-center">
              <div className="relative w-[120px] h-[120px] mb-4">
                <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                  <circle cx="50" cy="50" r="42" fill="none" stroke="#1F2937" strokeWidth="6" />
                  <circle
                    cx="50" cy="50" r="42" fill="none"
                    stroke="url(#healthGrad)"
                    strokeWidth="6"
                    strokeLinecap="round"
                    strokeDasharray={`${analytics.health_score.total * 2.64} 264`}
                    style={{ transition: 'stroke-dasharray 1.2s cubic-bezier(0.4,0,0.2,1)' }}
                  />
                  <defs>
                    <linearGradient id="healthGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor={analytics.health_score.total >= 70 ? '#10B981' : analytics.health_score.total >= 40 ? '#F59E0B' : '#EF4444'} />
                      <stop offset="100%" stopColor={analytics.health_score.total >= 70 ? '#34D399' : analytics.health_score.total >= 40 ? '#FBBF24' : '#F87171'} />
                    </linearGradient>
                  </defs>
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-3xl font-bold tabular-nums" data-testid="health-score-value">{analytics.health_score.total}</span>
                  <span className="text-[10px] text-[#6B7280] tracking-wider uppercase">score</span>
                </div>
              </div>
              <div className="w-full space-y-2.5 text-xs">
                {[
                  { label: 'Savings Rate', value: analytics.health_score.savings_rate, max: 25, color: '#10B981' },
                  { label: 'Budget Control', value: analytics.health_score.budget_control, max: 25, color: '#3B82F6' },
                  { label: 'Consistency', value: analytics.health_score.consistency, max: 25, color: '#F59E0B' },
                  { label: 'Goal Progress', value: analytics.health_score.goal_progress, max: 25, color: '#8B5CF6' },
                ].map((item) => (
                  <div key={item.label}>
                    <div className="flex justify-between text-[#6B7280] mb-1">
                      <span>{item.label}</span>
                      <span className="tabular-nums">{item.value}/{item.max}</span>
                    </div>
                    <div className="h-1.5 bg-[#1F2937] rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{ 
                          width: `${(item.value / item.max) * 100}%`, 
                          backgroundColor: item.color,
                          transition: 'width 0.8s cubic-bezier(0.4,0,0.2,1)'
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-32 text-[#6B7280] text-sm">Loading...</div>
          )}
        </div>

        {/* AI Insights Card */}
        <div className="lg:col-span-2">
          <div 
            className="card-ai p-6 overflow-hidden h-full animate-pulse-glow"
            data-testid="ai-insights-card"
          >
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-9 h-9 rounded-lg gradient-ai flex items-center justify-center shadow-lg shadow-violet-500/20">
                  <Sparkle size={18} weight="duotone" className="text-white" />
                </div>
                <div>
                  <h3 className="font-semibold">AI Insights</h3>
                  <p className="text-xs text-[#6B7280]">Powered by GPT</p>
                </div>
                <Link 
                  to="/insights" 
                  className="ml-auto flex items-center gap-1.5 text-xs font-medium text-[#8B5CF6] hover:text-[#A78BFA] transition-colors bg-[#8B5CF6]/10 rounded-full px-3 py-1.5"
                  data-testid="view-insights-link"
                >
                  View all
                  <ArrowRight size={12} weight="bold" />
                </Link>
              </div>
              
              {insightsLoading ? (
                <div className="flex items-center gap-2 text-[#6B7280]">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Loading insights...</span>
                </div>
              ) : insights?.content ? (
                <div className="text-[#D1D5DB] text-sm leading-relaxed whitespace-pre-line">
                  {insights.content.slice(0, 350)}
                  {insights.content.length > 350 && '...'}
                </div>
              ) : (
                <div className="flex flex-col items-center py-6 text-center">
                  <div className="w-12 h-12 rounded-xl bg-[#8B5CF6]/10 flex items-center justify-center mb-3">
                    <Lightning size={24} weight="duotone" className="text-[#8B5CF6]" />
                  </div>
                  <p className="text-[#9CA3AF] text-sm">No insights yet</p>
                  <p className="text-[#6B7280] text-xs mt-1">Generate your first AI analysis on the Insights page</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Analytics Charts */}
      {analytics && (
        <div className="grid lg:grid-cols-3 gap-5" data-testid="analytics-section">
          {/* Spending by Category - Pie */}
          <div className="card-default p-6" data-testid="spending-pie-chart">
            <div className="flex items-center gap-2.5 mb-5">
              <div className="w-8 h-8 rounded-lg bg-violet-500/10 flex items-center justify-center">
                <ChartPie size={16} weight="duotone" className="text-[#8B5CF6]" />
              </div>
              <h3 className="font-semibold text-sm">Spending by Category</h3>
            </div>
            {analytics.spending_by_category?.length > 0 ? (
              <div className="flex flex-col items-center">
                <ResponsiveContainer width="100%" height={175}>
                  <PieChart>
                    <Pie
                      data={analytics.spending_by_category}
                      cx="50%"
                      cy="50%"
                      innerRadius={48}
                      outerRadius={72}
                      dataKey="value"
                      strokeWidth={0}
                    >
                      {analytics.spending_by_category.map((_, i) => (
                        <Cell key={`cat-${analytics.spending_by_category[i].name}`} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={CHART_TOOLTIP_STYLE}
                      itemStyle={CHART_ITEM_STYLE}
                      formatter={(val) => formatCurrency(val)}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-3 justify-center">
                  {analytics.spending_by_category.map((item, i) => (
                    <div key={item.name} className="flex items-center gap-1.5 text-xs text-[#9CA3AF]">
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                      {item.name}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-[#6B7280] text-sm text-center py-12">No expenses yet</p>
            )}
          </div>

          {/* Monthly Trend - Line */}
          <div className="card-default p-6" data-testid="monthly-trend-chart">
            <div className="flex items-center gap-2.5 mb-5">
              <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <TrendUp size={16} weight="duotone" className="text-[#10B981]" />
              </div>
              <h3 className="font-semibold text-sm">Monthly Trend</h3>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={analytics.monthly_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                <XAxis dataKey="month" tick={AXIS_TICK} axisLine={false} tickLine={false} />
                <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} width={40} />
                <Tooltip
                  contentStyle={CHART_TOOLTIP_STYLE}
                  itemStyle={CHART_ITEM_STYLE}
                  formatter={(val) => formatCurrency(val)}
                />
                <Line type="monotone" dataKey="income" stroke="#10B981" strokeWidth={2} dot={INCOME_DOT} activeDot={INCOME_ACTIVE_DOT} name="Income" />
                <Line type="monotone" dataKey="expenses" stroke="#EF4444" strokeWidth={2} dot={EXPENSE_DOT} activeDot={EXPENSE_ACTIVE_DOT} name="Expenses" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Budget vs Actual - Bar */}
          <div className="card-default p-6" data-testid="budget-vs-actual-chart">
            <div className="flex items-center gap-2.5 mb-5">
              <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <ChartBar size={16} weight="duotone" className="text-[#3B82F6]" />
              </div>
              <h3 className="font-semibold text-sm">Budget vs Actual</h3>
            </div>
            {analytics.budget_vs_actual?.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={analytics.budget_vs_actual} barGap={2}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                  <XAxis dataKey="category" tick={AXIS_TICK_SM} axisLine={false} tickLine={false} />
                  <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} width={40} />
                  <Tooltip
                    contentStyle={CHART_TOOLTIP_STYLE}
                    itemStyle={CHART_ITEM_STYLE}
                    formatter={(val) => formatCurrency(val)}
                  />
                  <Bar dataKey="budget" fill="#3B82F6" radius={BAR_RADIUS} name="Budget" barSize={14} />
                  <Bar dataKey="actual" fill="#F59E0B" radius={BAR_RADIUS} name="Actual" barSize={14} />
                  <Legend wrapperStyle={LEGEND_STYLE} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-[#6B7280] text-sm text-center py-12">No budgets set</p>
            )}
          </div>
        </div>
      )}

      {/* Quick Actions Row */}
      <div className="grid sm:grid-cols-3 gap-4">
        {/* Can I Afford This? */}
        <Link 
          to="/affordability"
          className="card-default p-5 flex items-center gap-4 hover:border-[#3B82F6]/20 transition-all group"
          data-testid="afford-link"
        >
          <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
            <ShoppingCart size={20} weight="duotone" className="text-[#3B82F6]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm">Can I afford this?</p>
            <p className="text-xs text-[#6B7280]">Check before you spend</p>
          </div>
          <ArrowRight size={16} className="text-[#6B7280] group-hover:text-[#3B82F6] transition-colors flex-shrink-0" />
        </Link>

        {/* Top Spending Category */}
        {data?.top_category?.name && (
          <div className="card-default p-5" data-testid="top-category-card">
            <p className="text-xs text-[#6B7280] mb-2">Top spending this month</p>
            <p className="font-medium">{data.top_category.name}</p>
            <p className="text-[#F59E0B] font-semibold tabular-nums text-lg mt-0.5">
              {formatCurrency(data.top_category.amount)}
            </p>
          </div>
        )}

        {/* Savings Goal Progress */}
        {data?.savings_progress?.target > 0 && (
          <div className="card-default p-5" data-testid="savings-progress-card">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs text-[#6B7280]">Savings Goal</p>
              <span className="text-xs font-semibold text-[#8B5CF6] tabular-nums">
                {Math.round(data.savings_progress.percentage)}%
              </span>
            </div>
            <Progress 
              value={data.savings_progress.percentage} 
              className="h-2 bg-[#1F2937]"
            />
            <p className="text-xs text-[#6B7280] mt-2 tabular-nums">
              {formatCurrency(data.savings_progress.saved)} of {formatCurrency(data.savings_progress.target)}
            </p>
          </div>
        )}
      </div>

      {/* Recent Transactions */}
      <div className="card-default p-6" data-testid="recent-transactions">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-semibold">Recent Transactions</h3>
          <Link 
            to="/transactions" 
            className="text-xs font-medium text-[#3B82F6] hover:text-[#60A5FA] transition-colors"
            data-testid="view-all-transactions"
          >
            View all
          </Link>
        </div>
        
        {data?.recent_transactions?.length > 0 ? (
          <div className="space-y-1">
            {data.recent_transactions.map((t) => (
              <div 
                key={t.id} 
                className="flex items-center justify-between py-3 px-2 -mx-2 rounded-xl hover:bg-white/[0.02] transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                    t.type === 'income' ? 'bg-emerald-500/10' : 'bg-red-500/10'
                  }`}>
                    {t.type === 'income' ? (
                      <TrendUp size={18} weight="duotone" className="text-[#10B981]" />
                    ) : (
                      <TrendDown size={18} weight="duotone" className="text-[#EF4444]" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-sm">{t.category}</p>
                    <p className="text-xs text-[#6B7280]">{t.date}</p>
                  </div>
                </div>
                <p className={`font-semibold tabular-nums text-sm ${
                  t.type === 'income' ? 'text-[#10B981]' : 'text-[#EF4444]'
                }`}>
                  {t.type === 'income' ? '+' : '-'}{formatCurrency(t.amount)}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[#6B7280] text-center py-10 text-sm">
            No transactions yet. Add your first one!
          </p>
        )}
      </div>
    </div>
  );
}
